import { useCallback, useEffect, useMemo, useState } from "react";
import { MessageSquarePlus, UsersRound } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useHasPermission, useIsAdmin } from "@/hooks/use-permissions";
import { createInternalThread } from "@/lib/api/threads";
import { fetchInternalChatPartners } from "@/lib/api/users";
import { getAuthUser } from "@/lib/api/token-store";
import { avatarTintStyle, initialsOf } from "@/lib/dashboard/people";
import { requestOpenThreadMessage } from "@/lib/notifications/notification-navigation";
import {
  getSelfAvailability,
  getUserAvailability,
  isUserOnline,
  subscribePresence,
} from "@/lib/realtime/presence-service";
import { cn } from "@/lib/utils";

function ownerMatches(ownerUserId) {
  return String(getAuthUser()?.id || "").trim() === String(ownerUserId || "").trim();
}

function personName(person) {
  const first = String(person?.firstName || "").trim();
  const last = String(person?.lastName || "").trim();
  const full = `${first} ${last}`.trim();
  return full || String(person?.displayName || person?.name || "").trim() || String(person?.id || "");
}

function firstNameOf(name) {
  return String(name || "").trim().split(/\s+/)[0] || "them";
}


// Presence → dot tone + label. Ranked like the design sorts its roster: busy-ish first
// (someone is actively occupied), then available, then away/offline.
function statusOf({ online, availability }) {
  if (!online) return { tone: "off", label: "offline", rank: 2 };
  switch (availability) {
    case "busy":
      return { tone: "amb", label: "busy", rank: 0 };
    case "meeting":
      return { tone: "amb", label: "in a meeting", rank: 0 };
    case "dnd":
      return { tone: "amb", label: "do not disturb", rank: 0 };
    case "away":
      return { tone: "off", label: "away", rank: 2 };
    default:
      return { tone: "ok", label: "available", rank: 1 };
  }
}

/**
 * The workbench "Who's in" card: the staff directory with live presence dots (the same
 * feed that colors the Chats presence). Sorted busy → available → out; scrolls internally.
 *
 * Each colleague also carries a quick "Message" button: it find-or-creates the 1:1 staff
 * chat (the backend dedups direct threads) and hands the thread to the Chats page over the
 * open-thread bridge, exactly like the Patients page's message action.
 */
export function DashboardWhosInCard({ ownerUserId }) {
  const [roster, setRoster] = useState(null); // null = loading, [] = unavailable/empty
  // Presence deltas arrive over the WS; the tick invalidates the rows memo so it re-reads
  // the presence maps.
  const [presenceTick, setPresenceTick] = useState(0);
  // The person whose chat is being opened right now (button spinner + re-entry guard).
  const [messagingId, setMessagingId] = useState(null);
  // Starting an internal chat is a chats:manage action server-side (POST /threads/internal);
  // without it the button would only ever 403.
  const canMessage = useHasPermission("chats", "manage");
  const isAdmin = useIsAdmin();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await fetchInternalChatPartners();
        if (cancelled || !ownerMatches(ownerUserId)) return;
        const self = getAuthUser();
        const people = [
          ...(self?.id
            ? [{ id: String(self.id), name: personName(self), role: String(self.userType || "").trim() }]
            : []),
          ...response.items.map((person) => ({
            id: String(person?.id || "").trim(),
            name: personName(person),
            role: String(person?.userType || "").trim(),
          })),
        ].filter((person) => person.id);
        setRoster(people);
      } catch {
        if (!cancelled) setRoster([]);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [ownerUserId]);

  useEffect(
    () =>
      subscribePresence(() => {
        if (!ownerMatches(ownerUserId)) return;
        setPresenceTick((tick) => tick + 1);
      }),
    [ownerUserId],
  );

  // Who the caller may actually start a chat with. The roster above is the FULL staff
  // directory (the context-less call the Tasks assignee picker also uses); the chat
  // directory additionally applies the per-access-group visibility facet, and creating a
  // chat with someone outside it is refused. Admins bypass that facet server-side, so they
  // never need the extra call. null = no known restriction → show the button everywhere.
  const [messageableIds, setMessageableIds] = useState(null);
  useEffect(() => {
    if (!canMessage || isAdmin) {
      setMessageableIds(null);
      return undefined;
    }
    let cancelled = false;
    fetchInternalChatPartners({ context: "chat" })
      .then((response) => {
        if (cancelled || !ownerMatches(ownerUserId)) return;
        setMessageableIds(
          new Set(response.items.map((person) => String(person?.id || "").trim()).filter(Boolean)),
        );
      })
      .catch(() => {
        // Fall back to showing the button: the server still validates, and a refusal toasts.
        if (!cancelled) setMessageableIds(null);
      });
    return () => {
      cancelled = true;
    };
  }, [ownerUserId, canMessage, isAdmin]);

  // Find-or-create the 1:1 chat, then ask App/Chats to open it (App switches the page, the
  // messages hook opens the thread). Owner-guarded: a profile switch mid-request must not
  // navigate the incoming user to the outgoing user's chat.
  const handleMessage = useCallback(
    async (person) => {
      const partnerId = String(person?.id || "").trim();
      if (!partnerId || messagingId) return;
      setMessagingId(partnerId);
      try {
        const { threadId } = await createInternalThread({ userIds: [partnerId], kind: "direct" });
        if (!ownerMatches(ownerUserId)) return;
        if (!threadId) throw new Error("Could not open that chat.");
        requestOpenThreadMessage({ threadId, scope: "internal" });
      } catch (error) {
        if (ownerMatches(ownerUserId)) {
          toast.error(error?.message || "Could not open that chat.");
        }
      } finally {
        setMessagingId(null);
      }
    },
    [messagingId, ownerUserId],
  );

  const selfId = String(getAuthUser()?.id || "").trim();
  const rows = useMemo(() => {
    if (!Array.isArray(roster)) return [];
    return roster
      .map((person) => {
        const isSelf = person.id === selfId;
        const online = isSelf ? true : isUserOnline(person.id);
        const availability = isSelf ? getSelfAvailability() : getUserAvailability(person.id);
        return { ...person, status: statusOf({ online, availability }), online };
      })
      .sort(
        (a, b) => a.status.rank - b.status.rank || a.name.localeCompare(b.name),
      );
  }, [roster, selfId, presenceTick]);

  const onlineCount = rows.filter((row) => row.online).length;
  // You cannot chat with yourself, and non-admins only with chat-directory-visible staff.
  const isMessageable = (person) =>
    person.id !== selfId && (!messageableIds || messageableIds.has(person.id));

  return (
    <TooltipProvider delayDuration={300}>
      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[14px] px-[22px] py-5 max-[1199px]:max-h-[380px]">
        <div className="mb-3.5 flex flex-none items-baseline justify-between gap-3">
          <div className="flex items-center gap-[9px] text-[15.5px] font-black tracking-[-0.1px]">
            <UsersRound className="h-4 w-4 text-primary" />
            Who&apos;s in
          </div>
          {rows.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {onlineCount} of {rows.length}
            </span>
          )}
        </div>
        <ScrollArea className="min-h-0 flex-1">
          {roster === null && (
            <div className="flex flex-col gap-3 py-1">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="h-[30px] w-[30px] rounded-full" />
                  <Skeleton className="h-8 flex-1" />
                </div>
              ))}
            </div>
          )}
          {Array.isArray(roster) && rows.length === 0 && (
            <div className="py-6 text-center text-[12.5px] text-muted-foreground">
              Team presence is unavailable.
            </div>
          )}
          <div className="flex flex-col">
            {rows.map((person) => (
              <div key={person.id} className="group flex items-center gap-3 py-2">
                <div
                  className="grid h-[30px] w-[30px] flex-none place-items-center rounded-full border-[2.5px] border-card text-[10.5px] font-bold"
                  style={avatarTintStyle(person.id)}
                  aria-hidden
                >
                  {initialsOf(person.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13.5px] font-bold">
                    {person.name}
                    {person.id === selfId && (
                      <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">you</span>
                    )}
                  </div>
                  {person.role && (
                    <div className="truncate text-xs capitalize text-muted-foreground">{person.role}</div>
                  )}
                </div>
                <span className="flex flex-none items-center gap-1.5 text-[11.5px] font-bold text-muted-foreground">
                  <span
                    className={cn(
                      "h-2 w-2 rounded-full",
                      person.status.tone === "ok" && "bg-ok",
                      person.status.tone === "amb" && "bg-amb",
                      person.status.tone === "off" && "bg-border",
                    )}
                    aria-hidden
                  />
                  {person.status.label}
                </span>
                {!canMessage ? null : !isMessageable(person) ? (
                  // Keeps every row's status text on one right edge, button or not.
                  <span className="h-7 w-7 flex-none" aria-hidden />
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => handleMessage(person)}
                        disabled={messagingId === person.id}
                        aria-label={`Message ${person.name}`}
                        className={cn(
                          "grid h-7 w-7 flex-none place-items-center rounded-md text-muted-foreground opacity-40 transition-[opacity,color,background-color] hover:bg-muted hover:text-primary focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring group-hover:opacity-100",
                          // The in-flight row keeps its spinner visible even off-hover.
                          messagingId === person.id && "opacity-100",
                        )}
                      >
                        {messagingId === person.id ? (
                          <Spinner className="h-3.5 w-3.5" />
                        ) : (
                          <MessageSquarePlus className="h-3.5 w-3.5" aria-hidden />
                        )}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      Message {firstNameOf(person.name)}
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            ))}
          </div>
        </ScrollArea>
      </Card>
    </TooltipProvider>
  );
}
